import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useInvoices } from './api';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANT,
  INVOICE_TYPE_LABELS,
} from './labels';
import type { Invoice, InvoiceStatus, InvoiceType } from './types';

const STATUSES = Object.keys(INVOICE_STATUS_LABELS) as InvoiceStatus[];
const TYPES = Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[];

export function InvoicesPage() {
  const pagination = usePagination();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(params.get('overdueOnly') === 'true');
  const [sort, setSort] = useState('');

  const debounced = useDebounce(search);
  const { data, isLoading, error, refetch } = useInvoices({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    type: type || undefined,
    overdueOnly: overdueOnly ? 'true' : undefined,
    sort: sort || undefined,
  });

  const meta = data?.meta as
    | (typeof data extends undefined ? never : { totalInvoiced?: string; totalOutstanding?: string })
    | undefined;

  const columns: Column<Invoice>[] = [
    {
      key: 'number',
      header: 'Invoice',
      sortKey: 'number',
      cell: (row) => (
        <div className="min-w-0">
          <div className={cn('font-medium', row.isDraft && 'text-muted-foreground')}>
            {row.isDraft ? 'Draft' : row.number}
          </div>
          <div className="truncate text-xs text-muted-foreground">{row.company.name}</div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      className: 'text-sm text-muted-foreground',
      cell: (row) => INVOICE_TYPE_LABELS[row.type],
    },
    {
      key: 'issued',
      header: 'Issued',
      sortKey: 'issue_date',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.issueDate),
    },
    {
      key: 'due',
      header: 'Due',
      sortKey: 'due_date',
      className: 'whitespace-nowrap text-sm',
      cell: (row) => {
        const overdue =
          new Date(row.dueDate) < new Date() && row.balanceDue !== '0.00' && !row.isDraft;
        return (
          <span className={overdue ? 'font-medium text-destructive' : 'text-muted-foreground'}>
            {formatDate(row.dueDate)}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: 'Total',
      sortKey: 'grand_total',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.grandTotal, row.currency),
    },
    {
      key: 'balance',
      header: 'Outstanding',
      sortKey: 'balance_due',
      className: 'tabular text-sm',
      cell: (row) =>
        row.balanceDue === '0.00' ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          formatMoney(row.balanceDue, row.currency)
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      cell: (row) => (
        <Badge variant={INVOICE_STATUS_VARIANT[row.status]}>
          {INVOICE_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Invoices"
        description="Approving an invoice fixes it and posts it to the ledger."
        actions={
          <Can permission="finance.invoice.create">
            <Button onClick={() => navigate('/finance/invoices/new')}>
              <Plus className="h-4 w-4" />
              New invoice
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search number, PO or company…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {INVOICE_STATUS_LABELS[value]}
            </option>
          ))}
        </Select>
        <Select
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All types</option>
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {INVOICE_TYPE_LABELS[value]}
            </option>
          ))}
        </Select>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(event) => {
              setOverdueOnly(event.target.checked);
              pagination.reset();
            }}
          />
          Overdue only
        </label>

        {meta?.totalOutstanding && (
          <div className="ms-auto text-sm text-muted-foreground">
            <span className="tabular font-medium text-foreground">
              {formatMoney(meta.totalOutstanding)}
            </span>{' '}
            outstanding of{' '}
            <span className="tabular font-medium text-foreground">
              {formatMoney(meta.totalInvoiced)}
            </span>{' '}
            invoiced
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load invoices.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => navigate(`/finance/invoices/${row.id}`)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No invoices yet"
        emptyDescription="Raise one from a won deal, or start from scratch."
        emptyAction={
          <Can permission="finance.invoice.create">
            <Button onClick={() => navigate('/finance/invoices/new')}>
              <FileText className="h-4 w-4" />
              New invoice
            </Button>
          </Can>
        }
      />
    </div>
  );
}
